<template>
    <!-- Phone screen wrapper: 390 px (iPhone 16 base), centered, scales down on narrow panels -->
    <div
        ref="wrapper"
        class="mx-auto overflow-hidden"
        :style="wrapperStyle"
    >
        <!-- Device chrome: status bar (time + signal/wifi/battery) — purely cosmetic -->
        <div
            class="flex items-center justify-between font-semibold"
            :style="statusBarStyle"
            aria-hidden="true"
        >
            <span :style="{ color: navTextColor }">9:41</span>
            <div class="flex items-center" :style="{ gap: `${4 * ratio}px`, color: navTextColor }">
                <svg
                    :width="16 * ratio"
                    :height="11 * ratio"
                    viewBox="0 0 16 11"
                    fill="currentColor"
                >
                    <rect x="0"  y="7" width="3" height="4"  rx="0.5" />
                    <rect x="4"  y="5" width="3" height="6"  rx="0.5" />
                    <rect x="8"  y="2" width="3" height="9"  rx="0.5" />
                    <rect x="12" y="0" width="3" height="11" rx="0.5" />
                </svg>
                <feather-icon icon="WifiIcon" :style="chromeIconStyle(14)" />
                <feather-icon icon="BatteryIcon" :style="chromeBatteryStyle" />
            </div>
        </div>

        <!-- Top navigation bar: app primary_color background + optional content (slot) -->
        <div class="flex items-center" :style="headerStyle">
            <slot name="header" v-bind="slotScope" />
        </div>

        <!-- Scrollable body: fixed height, app background -->
        <div
            ref="scrollBody"
            class="menu-grid__scroll-body"
            :style="bodyStyle"
        >
            <slot v-bind="slotScope" />
        </div>

        <!-- Footer bar: same colour as header by default; optional content (slot) -->
        <div :style="footerStyle">
            <slot name="footer" v-bind="slotScope" />
        </div>

        <!-- Device chrome: home indicator pill — purely cosmetic -->
        <div
            class="flex items-center justify-center"
            :style="homeIndicatorStyle"
            aria-hidden="true"
        >
            <div :style="homeIndicatorPillStyle"></div>
        </div>
    </div>
</template>

<script>
import { Version } from '@/modules/Apps/Models/Version'
import {
    argbToRgba,
    computeRatio,
    loadMenuFont,
    buildPhoneWrapperStyle,
    buildHeaderStyle,
    buildBodyStyle,
    buildFooterStyle,
    buildStatusBarStyle,
    buildHomeIndicatorStyle,
    buildHomeIndicatorPillStyle,
} from '@/modules/Apps/Helpers/menuVisuals'
import { getMenuStyles } from '@/modules/Apps/Helpers/menuContext'
import {
    getBackgroundImageUrl,
    getResourcesBaseUrl,
} from '@/modules/Apps/Helpers/resourcesUrl'
import {
    BASE_WIDTH,
    BODY_HEIGHT,
    HEADER_MIN_HEIGHT,
    FOOTER_MIN_HEIGHT_DEFAULT,
    EDGE_SCROLL_ZONE_PX,
    EDGE_SCROLL_MAX_PX,
    STATUS_BAR_HEIGHT,
    STATUS_BAR_FONT_SIZE,
    STATUS_BAR_HORIZ_PADDING,
    HOME_INDICATOR_HEIGHT,
    HOME_INDICATOR_PILL_WIDTH,
    HOME_INDICATOR_PILL_HEIGHT,
} from '@/modules/Apps/Components/configurator/_shared/phoneDimensions'

export default {
    name: 'PhoneScreen',

    props: {
        menu:            { type: Object,  required: true },
        version:         { type: Version, required: true },
        // Base dimensions at ratio 1 (iPhone 16 logical points)
        baseWidth:       { type: Number, default: BASE_WIDTH },
        bodyHeight:      { type: Number, default: BODY_HEIGHT },
        headerMinHeight: { type: Number, default: HEADER_MIN_HEIGHT },
        footerMinHeight: { type: Number, default: FOOTER_MIN_HEIGHT_DEFAULT },
    },

    data() {
        return {
            containerWidth: this.baseWidth,
            fontName: null,
        }
    },

    computed: {
        styles() {
            return getMenuStyles(this.menu, this.version)
        },
        ratio() {
            return computeRatio(this.containerWidth)
        },
        navTextColor() {
            return this.styles.navigationbar_text_color
                ? argbToRgba(this.styles.navigationbar_text_color)
                : '#ffffff'
        },
        // Scoped-slot bindings — pruned to only what consumers actually use.
        slotScope() {
            return {
                ratio: this.ratio,
                containerWidth: this.containerWidth,
                navTextColor: this.navTextColor,
                fontName: this.fontName,
            }
        },
        wrapperStyle() {
            return buildPhoneWrapperStyle(this.baseWidth, this.ratio)
        },
        headerStyle() {
            return buildHeaderStyle(this.styles, this.ratio, this.headerMinHeight)
        },
        bodyStyle() {
            return buildBodyStyle(
                this.styles,
                this.ratio,
                this.bodyHeight,
                this.menu.type,
                this.backgroundImageUrl(),
            )
        },
        footerStyle() {
            return buildFooterStyle(this.styles, this.ratio, this.footerMinHeight)
        },
        statusBarStyle() {
            return buildStatusBarStyle(
                this.styles,
                this.ratio,
                STATUS_BAR_HEIGHT,
                STATUS_BAR_FONT_SIZE,
                STATUS_BAR_HORIZ_PADDING,
            )
        },
        homeIndicatorStyle() {
            return buildHomeIndicatorStyle(this.styles, this.ratio, HOME_INDICATOR_HEIGHT)
        },
        homeIndicatorPillStyle() {
            return buildHomeIndicatorPillStyle(
                this.navTextColor,
                this.ratio,
                HOME_INDICATOR_PILL_WIDTH,
                HOME_INDICATOR_PILL_HEIGHT,
            )
        },
        // Battery is wider than tall (icon is rectangular), so it gets its own sizing
        chromeBatteryStyle() {
            return { width: `${22 * this.ratio}px`, height: `${11 * this.ratio}px` }
        },
    },

    async mounted() {
        // requestAnimationFrame-throttled ResizeObserver — coalesces rapid resize
        // events into one update per frame.
        this._resizeFrame = null
        this._resizeObserver = new ResizeObserver(entries => {
            const width = entries[0] && entries[0].contentRect.width
            if (!(width > 0)) return
            if (this._resizeFrame !== null) return
            this._resizeFrame = requestAnimationFrame(() => {
                this._resizeFrame = null
                this.containerWidth = width
            })
        })
        this._resizeObserver.observe(this.$refs.wrapper)

        if (this.styles && this.styles.home_font) {
            await loadMenuFont(this.styles.home_font, getResourcesBaseUrl(this.$route.params.uuid, this.$route.params.versionUuid))
            this.fontName = this.styles.home_font
        }
    },

    beforeDestroy() {
        if (this._resizeFrame !== null) cancelAnimationFrame(this._resizeFrame)
        if (this._resizeObserver) this._resizeObserver.disconnect()
    },

    methods: {
        // CDN background image URL for the current menu type / season
        backgroundImageUrl() {
            return getBackgroundImageUrl(
                this.$route.params.uuid,
                this.$route.params.versionUuid,
                this.menu.type,
                this.menu.timeofyear,
            )
        },

        /** Public: scroll the body to the top (parent calls after navigation). */
        scrollToTop() {
            const el = this.$refs.scrollBody
            if (el) el.scrollTop = 0
        },

        /** Public: auto-scroll the body when the cursor is near top/bottom edge during a drag. */
        applyEdgeScroll(clientY) {
            const container = this.$refs.scrollBody
            if (!container) return
            const rect = container.getBoundingClientRect()
            const distTop    = clientY - rect.top
            const distBottom = rect.bottom - clientY
            if (distTop > 0 && distTop < EDGE_SCROLL_ZONE_PX) {
                container.scrollTop -= (1 - distTop / EDGE_SCROLL_ZONE_PX) * EDGE_SCROLL_MAX_PX
            } else if (distBottom > 0 && distBottom < EDGE_SCROLL_ZONE_PX) {
                container.scrollTop += (1 - distBottom / EDGE_SCROLL_ZONE_PX) * EDGE_SCROLL_MAX_PX
            }
        },

        /** Public: DOM ref to the scrollable body — used by DnD hit-testing. */
        getScrollElement() {
            return this.$refs.scrollBody
        },

        /**
         * Public: scroll a cell into view inside the body. Uses
         * `block: 'nearest'` so cells already visible don't trigger any
         * movement; off-screen cells animate into view.
         */
        scrollToCell(uuid) {
            const container = this.$refs.scrollBody
            if (!container || !uuid) return
            const cell = container.querySelector(`[data-uuid="${uuid}"]`)
            if (cell && typeof cell.scrollIntoView === 'function') {
                cell.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            }
        },

        /** Squared sizing for chrome icons (wifi). Battery uses chromeBatteryStyle. */
        chromeIconStyle(size) {
            const px = `${size * this.ratio}px`
            return { width: px, height: px }
        },
    },
}
</script>
